<script setup>
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { ErrorMessage, Field } from "vee-validate";
import { createPopper } from "@popperjs/core";

import { BIconCheck } from "bootstrap-icons-vue";

const props = defineProps({
  placeholder: {
    type: String,
    default: "Select",
  },
  name: {
    type: String,
    default: "",
  },
  selectId: {
    type: String,
    default: "exampleSelect",
  },
  options: {
    type: Array,
    default: [],
    required: true,
  },
  specificListOfOptionsToFindOptionOnFirstLoad: {
    type: Array,
    default: [],
  },
  modelValue: {
    type: [Array, String, Number, null],
    default: null,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  tryToFindNameInOptionsOnFirstLoad: {
    type: Boolean,
    default: true,
  },
  label: {
    type: String,
    default: "",
  },
  hasBoldLabel: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  appendToBody: {
    type: Boolean,
    default: false,
  },
  background: {
    type: String,
    default: "transparent",
  },
  reduce: {
    type: Function,
    default: (option) => option,
  },
  resizable: {
    type: Boolean,
    default: false,
  },
  sortOptions: {
    type: Boolean,
    default: true,
  },
  filterable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["update:modelValue", "search"]);

const selected = ref();
const inputWrapperRef = ref(null);

const onCheckboxClick = (optionValue) => {
  const truncatedOption =
    typeof optionValue === "object" ? optionValue.value : optionValue;
  const isSelected = selected.value
    ?.map((option) => {
      return typeof option === "object" ? option.value : option;
    })
    ?.includes(truncatedOption);

  if (isSelected) {
    selected.value = selected.value.filter((value) => {
      const truncatedValue = typeof value === "object" ? value.value : value;
      return truncatedValue !== truncatedOption;
    });
  } else {
    selected.value = Array.isArray(selected.value)
      ? selected.value.concat([optionValue])
      : [optionValue];
  }
};

const resetSearch = () => {
  inputWrapperRef.value.search = "";
};

watch(
  () => props.modelValue,
  (newValue, prevValue) => {
    if (
      Array.isArray(newValue) &&
      Array.isArray(prevValue) &&
      JSON.stringify(newValue) === JSON.stringify(prevValue)
    ) {
      return;
    }
    if (selected.value === newValue) {
      return;
    }
    selected.value = newValue;
  }
);

const selectedOnThisFocus = ref([]);

const onModelValueChange = () => {
  const options = props.specificListOfOptionsToFindOptionOnFirstLoad.length
    ? props.specificListOfOptionsToFindOptionOnFirstLoad
    : props.options;

  if (props.tryToFindNameInOptionsOnFirstLoad) {
    const validTypes = ["string", "number"];
    if (
      validTypes.includes(typeof props.modelValue) || (
        Array.isArray(props.modelValue) &&
          props.modelValue.every((item) => validTypes.includes(typeof item))
      )
    ) {
      if (Array.isArray(props.modelValue)) {
        let mapped = props.modelValue.map((item) => {
          const option = options.find((optionData) => {
            return optionData.value === item;
          });
          if (option) {
            return option;
          }
          return item;
        });
        if (typeof props.reduce === "function") {
          mapped = mapped.map(props.reduce);
        }

        selected.value = mapped;
      }
      else {
        let value =
          options.find((optionData) => optionData.value === props.modelValue) ||
          props.modelValue;

        if (value) {
          if (typeof props.reduce === "function" && typeof value === "object") {
            value = props.reduce(value);
          }
          selected.value = props.multiple ? [value] : value;
        }
      }
    }
  }
  else {
    let value = props.modelValue;

    if (typeof props.reduce === "function" && typeof value === "object") {
      value = props.reduce(value);
    }

    selected.value = value;
  }
};

onMounted(() => {
  // Initialization logic

  onModelValueChange();

  // Apply watch only after onMounted logic has completed
  watch(
    selected,
    (newValue, oldValue) => {
      if (isFocused.value) {
        if (!oldValue) {
          oldValue = [];
        }

        const addedItems = newValue.filter((item) => !oldValue.includes(item));

        selectedOnThisFocus.value =
          selectedOnThisFocus.value.concat(addedItems);
      }

      if (
        newValue &&
        typeof newValue === "object" &&
        !Array.isArray(newValue) &&
        !Object.keys(newValue)?.length
      ) {
        newValue = props.multiple ? [] : null;
      }

      resetSearch();
      emit("update:modelValue", newValue);
    },
    {
      deep: true,
    }
  );
});

// GK NOTE: THIS IS CAUSING CIRCULAR CALLS - implement this differently or use a different input for this
// watch(
//   () => props.modelValue,
//   () => {
//     onModelValueChange();
//   },
//   { deep: true }
// );

const selectedSimple = computed(() => {
  return (
    selected?.value?.map?.((option) => {
      return typeof option === "object" ? option.value : option;
    }) || [selected?.value]
  );
});

const sortedOptions = computed(() => {
  if (!props.sortOptions) {
    return props.options;
  }

  const selectedValues = selectedSimple.value || [];

  const selectedItems = props.options.filter(
    (option) =>
      selectedValues.includes(option.value) &&
      !selectedOnThisFocus.value.includes(option.value)
  );

  const unselectedItems = props.options.filter(
    (option) =>
      !selectedValues.includes(option.value) ||
      selectedOnThisFocus.value.includes(option.value)
  );

  selectedItems.sort((a, b) => a.text.localeCompare(b.text));
  unselectedItems.sort((a, b) => a.text.localeCompare(b.text));

  return [...selectedItems, ...unselectedItems];
});

const isFocused = computed(() => {
  return inputWrapperRef.value?.open;
});

watch(
  isFocused,
  async () => {
    if (!isFocused.value) {
      selectedOnThisFocus.value = [];
    }
  },
  {
    deep: true,
  }
);

const withPopper = (dropdownList, component, { width }) => {
  /**
   * We need to explicitly define the dropdown width since
   * it is usually inherited from the parent with CSS.
   */
  dropdownList.style.width = width;

  /**
   * Here we position the dropdownList relative to the $refs.toggle Element.
   *
   * The 'offset' modifier aligns the dropdown so that the $refs.toggle and
   * the dropdownList overlap by 1 pixel.
   *
   * The 'toggleClass' modifier adds a 'drop-up' class to the Vue Select
   * wrapper so that we can set some styles for when the dropdown is placed
   * above.
   */
  const popper = createPopper(component.$refs.toggle, dropdownList, {
    placement: "bottom",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, -1],
        },
      },
      {
        name: "toggleClass",
        enabled: true,
        phase: "write",
        fn({ state }) {
          component.$el.classList.toggle("drop-up", state.placement === "top");
        },
      },
    ],
  });

  /**
   * To prevent memory leaks Popper needs to be destroyed.
   * If you return function, it will be called just before dropdown is removed from DOM.
   */
  return () => popper.destroy();
};
</script>

<template>
  <div
    :class="[
      'select-inp-wrapper',
      { focused: isFocused },
      { resizable: resizable },
    ]"
  >
    <label
      v-if="label"
      for="input"
      :class="[
        'select-inp-label w-100 p-0 text-left text-decoration-none text-neutral-350',
        { 'fw-bold': hasBoldLabel },
      ]"
    >
      <slot>{{ label }}</slot>
    </label>
    <Field :name="name" v-model="selected" v-slot="{ meta }">
      <v-select
        ref="inputWrapperRef"
        v-model="selected"
        :id="selectId"
        :options="sortedOptions"
        :multiple="multiple"
        :reduce="reduce"
        searchable
        :filterable="filterable"
        deselectFromDropdown
        clearSearchOnSelect
        label="text"
        :autoscroll="false"
        :disabled="disabled"
        :placeholder="placeholder"
        :appendToBody="appendToBody"
        :calculate-position="withPopper"
        @search="(search, setLoading) => emit('search', search, setLoading)"
        class="custom-select w-100"
        :class="{
          'is-valid': meta.valid && meta.touched,
          'is-invalid': !meta.valid && meta.touched,
        }"
        :style="{ background: background, 'border-radius': '6px' }"
      >
        <template v-if="$slots['list-header']" #list-header>
          <slot name="list-header"></slot>
        </template>

        <template #open-indicator="{ attributes }">
          <span v-bind="attributes">
            <IconChevronDown :width="16" :height="16" />
          </span>
        </template>
        <template #option="props">
          <div
            class="d-flex align-items-center justify-content-between"
            v-if="multiple"
            @click.stop="onCheckboxClick(props.value)"
          >
            <!-- <CustomCheckbox
              class="select-inp-checkbox"
              :model-value="selectedSimple?.includes(props.value)"
              :value="true"
            /> -->
            <span class="text-neutral-400 font-size-sm">{{ props.text }}</span>
            <BIconCheck class="select-inp-icon-check" />
          </div>
          <div v-else class="d-flex align-items-center justify-content-between">
            <span class="text-neutral-400 font-size-sm">{{ props.text }}</span>
            <BIconCheck class="select-inp-icon-check" />
          </div>
        </template>
        <template #selected-option-container="{ option, multiple }">
          <slot name="selected-option" :option="option">
            <div class="vs__selected">
              {{ option.text }}
            </div>
          </slot>
        </template>

        <template
          v-if="$slots['no-options']"
          #no-options="{ search, searching, loading }"
          :search="search"
        >
          <slot name="no-options" :search="search"></slot>
        </template>

        <template
          v-if="$slots['list-footer']"
          #list-footer="{ search, searching, loading }"
          :search="search"
        >
          <slot name="list-footer" :search="search"></slot>
        </template>
      </v-select>
    </Field>
    <ErrorMessage
      as="div"
      :name="name"
      v-slot="{ message }"
      class="invalid-feedback"
    >
      {{ message }}
    </ErrorMessage>
  </div>
</template>

<style lang="scss">
.select-inp-icon-check {
  width: 24px;
  height: 24px;
  fill: var(--primary-250);
  display: none;
}

.select-inp-wrapper {
  input::placeholder {
    font-size: $font-size-sm;
    color: $neutral-300;
  }
  .vs--single .vs__selected {
    margin-top: 5px;
    width: 100%;
  }

  .invalid-feedback {
    margin-bottom: 4px;
    margin-top: 0.625rem;
    color: $red-150;
    font-size: $font-size-xs;
  }
}

.select-inp-label {
  font-size: $font-size-sm;
  color: $neutral-350;
  margin-bottom: 0.625rem;
}

.select-inp-checkbox {
  margin-right: 0.75rem;
}

.custom-select {
  height: 2.5rem;

  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  input {
    color: #334250;
  }
}

.vs__dropdown-toggle {
  border-radius: 8px;
}

.vs__actions {
  padding: 0 12px 0 0;
}

.vs__selected {
  margin: 0;
  font-size: $font-size-sm;
  color: #434343;
  background: $neutral-50;
  border: none;
}

.vs__selected-options {
  height: 40px !important;
  overflow-y: scroll !important;

  padding: 5px;

  -ms-overflow-style: none; /* for Internet Explorer, Edge */
  scrollbar-width: none; /* for Firefox */

  &::-webkit-scrollbar {
    display: none; /* for Chrome, Safari, and Opera */
  }
}

.vs__dropdown-toggle {
  height: 2.5rem;
  border: 1px solid $bluish-gray-200;
}

.vs__search {
  font-size: $font-size-sm;
  color: $neutral-400;
  margin: 0 !important;
}

.vs__dropdown-menu {
  --vs-dropdown-option--active-bg: #{var(--primary-0)};
  box-shadow: 0 2px 12px 0 rgba(125, 144, 165, 0.2);
  border: none;
  border-radius: 8px;
  padding: 4px;
  z-index: 1000000000000;

  .vs__dropdown-option {
    padding: 0;
  }

  li {
    font-size: $font-size-sm;
    color: $neutral-400;

    & > div {
      padding: 12px;
    }

    &:hover {
      background: var(--primary-0);
    }
  }
}

.vs__dropdown-option--selected {
  background: var(--primary-0);
  border: 1px solid var(--primary-50);
  border-radius: 8px;
  padding: 0px !important;

  .select-inp-icon-check {
    display: block;
  }
}

.vs--open {
  .vs__dropdown-toggle {
    border: 1px solid var(--primary-200) !important;
  }
}

.vs__clear {
  display: none;
}

.resizable {
  .custom-select {
    min-height: 2.5rem !important;
    height: fit-content !important;
  }

  .vs__dropdown-toggle {
    min-height: 2.5rem !important;
    height: fit-content !important;
  }

  .vs__selected-options {
    min-height: 40px !important;
    height: fit-content !important;
  }
}
</style>
